// Globals
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Models
import { IRedirect } from '@/models/redirect.model';

// Modules
import { selectChannel } from '@/modules/Channels';
import { Header } from '@/modules/Layout';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  createRedirect,
  deleteRedirect,
  getRedirects,
  updateRedirect,
  importRedirects,
  deleteRedirectsByQuery,
} from '../../feature';

// Components
import { Modal, ModalSize, showSuccessToast } from '@/components';
import { PlusIcon, TrashIcon } from '@/icons';
import { Button, Popover, Table } from '@/ui';
import { RedirectDeleteModalContent } from '../../components';
import RedirectsForm from '../../components/RedirectsForm/RedirectsForm';
import ImportRedirectsForm from '../../components/ImportRedirectsForm/ImportRedirectsForm';
import {
  PopoverOverlayGlobalStyles,
  StyledButtons,
  StyledContainer,
  StyledPagination,
  StyledTableHeader,
} from './RedirectsConatiner.styles';

// Helpers
import CreateRedirectDTO from '../../dtos/CreateRedirect.dto';
import { GetRedirectsDTO } from '../../dtos/GetRedirects.dto';
import UpdateRedirectDTO from '../../dtos/UpdateRedirect.dto';
import RedirectsImportDTO from '../../dtos/RedirectsImport.dto';
import { redirectsColumns } from '../../helpers/redirectColumns';
import {
  RedirectFormType,
  RedirectsFormFields,
  TCreateRedirectFormValues,
  TUpdateRedirectFormValues,
  TRedirectsImportFormValues,
} from '../../helpers/types';
import { useChosenRedirects, useGetRedirects, useSearchRedirects } from '../../hooks';

export const RedirectsContainer = () => {
  const { t } = useTranslation();
  const channel = useAppState(selectChannel);
  const dispatch = useAppDispatch();
  const { chosenRedirects, handleResetChosenRedirects, handleSelectOneRedirect, rowSelection } = useChosenRedirects();

  const { searchValue, handleSearchRedirects, handleResetSearchValue } = useSearchRedirects();

  const [selectedRedirect, setSelectedRedirect] = useState<IRedirect | null>(null);
  const [formType, setFormType] = useState<RedirectFormType | null>(null);

  const { isLoading, redirects, pagination } = useGetRedirects({
    channelId: channel?.id,
  });

  const handleDelete = (record: IRedirect) => {
    setSelectedRedirect(record);
    setFormType(RedirectFormType.DELETE);
  };

  const handleBulkDelete = () => {
    setFormType(RedirectFormType.BULK_DELETE);
  };

  const handleDeleteAll = () => {
    setFormType(RedirectFormType.DELETE_ALL);
  };

  const handleOpenEdit = (record: IRedirect) => {
    setSelectedRedirect(record);
    setFormType(RedirectFormType.UPDATE);
  };

  const handlePageChange = (page: number) => {
    const dto = new GetRedirectsDTO({ page, channelId: channel?.id ?? 0, query: searchValue });
    dispatch(getRedirects(dto));
  };

  const handleOpenCreate = () => {
    setSelectedRedirect(null);
    setFormType(RedirectFormType.CREATE);
  };

  const handleCloseModal = () => {
    setFormType(null);
  };

  const handleOpenImport = () => {
    setSelectedRedirect(null);
    setFormType(RedirectFormType.IMPORT);
  };

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedRedirect) return;
    await dispatch(deleteRedirect({ id: selectedRedirect.id })).unwrap();
    setSelectedRedirect(null);
    setFormType(null);
    handleSelectOneRedirect(selectedRedirect, false);
  }, [dispatch, selectedRedirect, handleSelectOneRedirect]);

  const handleResetStateAfterDelete = useCallback(async () => {
    if (!channel?.id) return;

    const getDto = new GetRedirectsDTO({ page: 1, channelId: channel.id });
    await dispatch(getRedirects(getDto)).unwrap();

    handleResetSearchValue();
    handleResetChosenRedirects();
    setFormType(null);
  }, [dispatch, handleResetSearchValue, handleResetChosenRedirects, channel?.id]);

  const handleConfirmBulkDelete = useCallback(async () => {
    if (!chosenRedirects.length || !channel?.id) return;

    await dispatch(deleteRedirectsByQuery({ ids: chosenRedirects, channelId: channel.id })).unwrap();
    await handleResetStateAfterDelete();
  }, [chosenRedirects, dispatch, channel?.id, handleResetStateAfterDelete]);

  const handleConfirmDeleteAll = useCallback(async () => {
    if (!channel?.id) return;

    await dispatch(deleteRedirectsByQuery({ search: searchValue, channelId: channel.id })).unwrap();
    await handleResetStateAfterDelete();
  }, [dispatch, channel?.id, searchValue, handleResetStateAfterDelete]);

  const handleSubmitEditRedirect = useCallback(
    async (values: TUpdateRedirectFormValues) => {
      if (!selectedRedirect?.id || !channel?.id) return;
      const updateDto = new UpdateRedirectDTO({ ...values, id: selectedRedirect?.id });
      try {
        await dispatch(updateRedirect(updateDto)).unwrap();
        setSelectedRedirect(null);
        setFormType(null);
      } catch {}
    },
    [channel?.id, dispatch, selectedRedirect?.id],
  );

  const handleSubmitRedirectsImport = useCallback(
    async (values: TRedirectsImportFormValues) => {
      setSelectedRedirect(null);
      if (!channel?.id) return;
      const importDto = new RedirectsImportDTO({ ...values, channelId: channel.id });
      try {
        const result = await dispatch(importRedirects(importDto)).unwrap();
        if (result.success) {
          showSuccessToast({ message: t('import_success_message') });
        }
        setFormType(null);
        const getDto = new GetRedirectsDTO({ page: 1, channelId: channel.id });
        await dispatch(getRedirects(getDto)).unwrap();
      } catch {}
    },
    [channel?.id, dispatch, t],
  );

  const handleSubmitCreateRedirect = useCallback(
    async (values: TCreateRedirectFormValues) => {
      setSelectedRedirect(null);
      if (!channel?.id) return;
      const createDto = new CreateRedirectDTO({ ...values, channelId: channel.id });
      const getDto = new GetRedirectsDTO({ channelId: channel.id, page: '1' });
      try {
        await dispatch(createRedirect(createDto)).unwrap();
        await dispatch(getRedirects(getDto)).unwrap();
        setFormType(null);
      } catch {}
    },
    [channel?.id, dispatch],
  );

  const getModalContent = useCallback(
    (type: RedirectFormType) => {
      switch (type) {
        case RedirectFormType.UPDATE:
          return (
            <RedirectsForm
              onSubmit={handleSubmitEditRedirect}
              isLoading={isLoading}
              formType={RedirectFormType.UPDATE}
              initialValues={{
                [RedirectsFormFields.SOURCE]: selectedRedirect?.source,
                [RedirectsFormFields.TARGET]: selectedRedirect?.target,
                [RedirectsFormFields.CODE]: selectedRedirect?.code,
              }}
            />
          );
        case RedirectFormType.DELETE:
          return (
            <RedirectDeleteModalContent
              btnTitle={t('delete_redirect')}
              onDelete={handleConfirmDelete}
              isLoading={isLoading}
              description={t('delete_description')}
            />
          );
        case RedirectFormType.CREATE:
          return (
            <RedirectsForm
              formType={RedirectFormType.CREATE}
              isLoading={isLoading}
              onSubmit={handleSubmitCreateRedirect}
            />
          );
        case RedirectFormType.IMPORT:
          return <ImportRedirectsForm onSubmit={handleSubmitRedirectsImport} isLoading={isLoading} />;
        case RedirectFormType.BULK_DELETE:
          return (
            <RedirectDeleteModalContent
              btnTitle={t('delete_redirects')}
              onDelete={handleConfirmBulkDelete}
              isLoading={isLoading}
              description={t('delete_description')}
            />
          );
        case RedirectFormType.DELETE_ALL:
          return (
            <RedirectDeleteModalContent
              btnTitle={t('delete_redirects')}
              onDelete={handleConfirmDeleteAll}
              isLoading={isLoading}
              description={t('delete_all_by_query_description', { entities: 'redirects' })}
            />
          );

        default:
          return null;
      }
    },
    [
      handleConfirmDelete,
      handleSubmitCreateRedirect,
      handleSubmitEditRedirect,
      handleSubmitRedirectsImport,
      handleConfirmBulkDelete,
      handleConfirmDeleteAll,
      isLoading,
      selectedRedirect?.code,
      selectedRedirect?.source,
      selectedRedirect?.target,
      t,
    ],
  );

  const title = useMemo(() => {
    switch (formType) {
      case RedirectFormType.UPDATE:
        return t('title_edit_redirect');
      case RedirectFormType.DELETE:
        return t('delete_redirect_confirm', { source: selectedRedirect?.source });
      case RedirectFormType.CREATE:
        return t('title_create_redirect');
      case RedirectFormType.IMPORT:
        return t('title_import_redirects');
      case RedirectFormType.BULK_DELETE:
        return t('bulk_delete_redirect_confirm', { count: chosenRedirects.length });
      case RedirectFormType.DELETE_ALL:
        return t('delete_all_redirects_confirm');
      default:
        return null;
    }
  }, [chosenRedirects.length, formType, selectedRedirect?.source, t]);

  return (
    <>
      <Header title={t('menu_redirects')} suptitle={t('menu_seo')}>
        <StyledButtons>
          <Button type="default" onClick={handleOpenImport}>
            {t('btn_redirects_import')}
          </Button>
          <Button icon={<PlusIcon />} type="primary" onClick={handleOpenCreate}>
            {t('btn_add_redirect')}
          </Button>
        </StyledButtons>
      </Header>
      <StyledContainer>
        <StyledTableHeader>
          {!!chosenRedirects.length && <p>{t('selected_redirects', { count: chosenRedirects.length })}</p>}
          <Popover
            rootClassName="bulk-delete-popover"
            title={!chosenRedirects.length ? 'Select items to delete' : null}
            trigger="hover"
          >
            <Button
              onClick={handleBulkDelete}
              disabled={!chosenRedirects.length}
              type="primary"
              icon={<TrashIcon />}
              danger
            >
              {t('btn_delete_selected')}
            </Button>
            <PopoverOverlayGlobalStyles />
          </Popover>
          <Button onClick={handleDeleteAll} icon={<TrashIcon />} danger>
            {t('btn_delete_all_by_query')}
          </Button>
        </StyledTableHeader>
        <Table
          rowSelection={rowSelection}
          columns={redirectsColumns(t, searchValue, handleSearchRedirects, handleDelete, handleOpenEdit)}
          rowKey="id"
          dataSource={redirects ?? []}
          loading={isLoading}
          pagination={false}
          size="small"
          rowHoverable
          virtual
        />
        <StyledPagination
          disabled={isLoading}
          total={pagination?.total ?? 0}
          current={pagination?.page ?? 1}
          pageSize={pagination?.pageSize ?? 10}
          onChange={handlePageChange}
          hideOnSinglePage
        />
      </StyledContainer>
      <Modal
        title={title}
        size={formType === RedirectFormType.DELETE ? ModalSize.MEDIUM : ModalSize.LARGE}
        visible={!!formType}
        onCancel={handleCloseModal}
        destroyOnClose
      >
        {!!formType && getModalContent(formType)}
      </Modal>
    </>
  );
};
